import { Link } from 'react-router-dom';
import { type InlineSpan, type LegalBlock, parseInlineSpans, parseLegalMarkdown } from '../lib/legalMarkdown';
import styles from './LegalDocument.module.css';

interface LegalDocumentProps {
  /** The raw markdown source (imported via Vite's `?raw` suffix from
   *  docs/legal/*.md — ONE source of truth, rendered here rather than
   *  forked into a JSX string; see Terms.tsx/Privacy.tsx's own import). */
  markdown: string;
}

/**
 * Renders a docs/legal/*.md file in-app. Used by both /terms and /privacy
 * (Terms.tsx / Privacy.tsx) so the EULA and Privacy Policy share ONE render
 * path — parseLegalMarkdown (lib/legalMarkdown.ts) does the actual parsing;
 * this component only maps the resulting block list into JSX.
 *
 * The "Working draft — attorney review pending" framing already lives IN the
 * source markdown itself (every docs/legal/*.md file opens with an HTML
 * comment + an H1 whose text says "DRAFT (ATTORNEY REVIEW REQUIRED)") — this
 * component does not re-assert or duplicate that framing separately; the
 * single source of truth includes its own honesty stamp.
 */
export function LegalDocument({ markdown }: LegalDocumentProps) {
  const blocks = parseLegalMarkdown(markdown);
  return (
    <div className={styles.doc}>
      {blocks.map((block, i) => (
        <LegalBlockView key={`${block.type}-${i}`} block={block} />
      ))}
    </div>
  );
}

function LegalBlockView({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'h1':
      return <h1 className={styles.h1}>{block.text}</h1>;
    case 'h2':
      return <h2 className={styles.h2}>{block.text}</h2>;
    case 'h3':
      return <h3 className={styles.h3}>{block.text}</h3>;
    case 'paragraph':
      return (
        <p className={styles.paragraph}>
          <InlineSpans spans={parseInlineSpans(block.text)} />
        </p>
      );
    case 'list':
      return (
        <ul className={styles.list}>
          {block.items.map((item, i) => (
            <li key={i} className={styles.listItem}>
              <InlineSpans spans={parseInlineSpans(item)} />
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {block.headers.map((h, i) => (
                  <th key={i} className={styles.th}>
                    <InlineSpans spans={parseInlineSpans(h)} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={styles.td}>
                      <InlineSpans spans={parseInlineSpans(cell)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'hr':
      return <hr className={styles.hr} />;
    default:
      return null;
  }
}

function InlineSpans({ spans }: { spans: InlineSpan[] }) {
  return (
    <>
      {spans.map((span, i) => {
        if (span.type === 'bold') return <strong key={i}>{span.text}</strong>;
        if (span.type === 'code') return <code key={i}>{span.text}</code>;
        if (span.type === 'link') {
          // Every link target in these docs is either a mailto:, an external
          // https:// URL, or a relative ./other-doc.md reference — the last
          // one is rewritten to the in-app route (see legalLinkHref below) so
          // "See our Privacy Policy" inside the EULA/Terms actually navigates
          // within the app rather than 404ing on a raw .md path.
          const href = legalLinkHref(span.href);
          const isInternal = href.startsWith('/');
          return isInternal ? (
            <Link key={i} to={href} className={styles.link}>
              {span.text}
            </Link>
          ) : (
            <a key={i} href={href} className={styles.link} target="_blank" rel="noreferrer">
              {span.text}
            </a>
          );
        }
        return <span key={i}>{span.text}</span>;
      })}
    </>
  );
}

/** Rewrites a relative `./privacy-policy.md` / `./terms-of-service.md` /
 *  `./eula.md` link (the exact shape terms-of-service.md §7 and this EULA's
 *  own header use to reference each other) to the in-app route. Anything
 *  else (mailto:, https://) passes through unchanged. Exported for direct
 *  unit coverage. */
export function legalLinkHref(rawHref: string): string {
  if (rawHref.endsWith('privacy-policy.md')) return '/privacy';
  if (rawHref.endsWith('terms-of-service.md')) return '/terms';
  if (rawHref.endsWith('eula.md')) return '/terms';
  return rawHref;
}
